import React, { useState } from 'react'
import axios from 'axios'
import { FaEye, FaEyeSlash, FaUser, FaLock, FaSpinner } from 'react-icons/fa'
import API_URLS from '../apiUrls'

const Login = ({ setUser, onShowRegister }) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async e => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      console.log('Attempting login for user:', username)
      console.log('Cookies before login:', document.cookie)

      const response = await axios.post(
        API_URLS.AUTH,
        { username, password },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )

      console.log('Login response:', response.data)
      console.log('Cookies after login:', document.cookie)

      if (response.data.status === 'success') {
        console.log('Login successful, setting user:', response.data.user)
        setUser(response.data.user)

        setTimeout(() => {
          console.log('Cookies 1 second after login:', document.cookie)
          if (document.cookie.length === 0) {
            console.log('⚠️ WARNING: No session cookie found after login!')
          }
        }, 1000)
      } else {
        console.log('Login failed:', response.data.message)
        setError(response.data.message || 'Login failed')
      }
    } catch (err) {
      console.error('Login error:', err)
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      })

      if (err.response?.data?.message) {
        setError(err.response.data.message)
      } else if (err.response?.status === 401) {
        setError('Invalid username or password')
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.')
      } else {
        setError('Login failed. Please check your connection and try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className='min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-blue-100 via-white to-teal-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
      <div className='w-full max-w-md mx-auto rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-0 overflow-hidden'>
        {/* Logo/Header inside card */}
        <div className='flex flex-col items-center justify-center pt-8 pb-4 px-8 bg-gradient-to-tr from-blue-50 via-white to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900'>
          <div className='flex items-center mb-2'>
            <svg className='h-8 w-8 text-teal-500 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <rect x='3' y='5' width='18' height='14' rx='3' fill='currentColor' className='text-teal-400'/>
              <rect x='7' y='9' width='10' height='2' rx='1' fill='white'/>
            </svg>
            <span className='text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight'>Cashbook</span>
          </div>
          <div className='rounded-full bg-gradient-to-r from-blue-500 to-teal-500 p-3 mb-2 shadow-lg'>
            <svg className='h-6 w-6 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' />
            </svg>
          </div>
          <h2 className='text-2xl font-extrabold text-gray-900 dark:text-white mb-1'>Welcome Back</h2>
          <p className='text-sm text-gray-600 dark:text-gray-400 mb-2'>Sign in to continue</p>
        </div>

        {/* Form Card */}
        <div className='px-8 pb-8 pt-4'>
          {error && (
            <div className='mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg'>
              <div className='flex items-start'>
                <svg className='w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0' fill='currentColor' viewBox='0 0 20 20'>
                  <path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z' clipRule='evenodd' />
                </svg>
                <span className='text-red-700 dark:text-red-300 text-sm font-medium leading-5'>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className='space-y-4'>
            {/* Username Field */}
            <div>
              <label className='block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300'>Username</label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <FaUser className='h-4 w-4 text-gray-400' />
                </div>
                <input
                  type='text'
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className='w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500 text-base shadow-sm'
                  placeholder='Enter username'
                  required
                  disabled={isLoading}
                  autoComplete='username'
                  autoCapitalize='off'
                  autoCorrect='off'
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className='block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300'>Password</label>
              <div className='relative'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <FaLock className='h-4 w-4 text-gray-400' />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className='w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white/80 dark:bg-gray-800/80 text-gray-900 dark:text-white transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500 text-base shadow-sm'
                  placeholder='Enter password'
                  required
                  disabled={isLoading}
                  autoComplete='current-password'
                />
                <button
                  type='button'
                  className='absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation'
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <FaEyeSlash className='h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors' />
                  ) : (
                    <FaEye className='h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors' />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type='submit'
              className={`w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 touch-manipulation text-base ${
                isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'shadow-lg hover:shadow-xl active:scale-95'
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className='flex items-center justify-center'>
                  <FaSpinner className='animate-spin -ml-1 mr-3 h-4 w-4' />
                  Signing in...
                </div>
              ) : (
                <div className='flex items-center justify-center'>
                  <svg className='h-5 w-5 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
                  </svg>
                  Sign In
                </div>
              )}
            </button>
          </form>

          {/* Register link above Need help */}
          <div className='mt-4 text-center text-sm'>
            Don't have an account?{' '}
            <button
              type='button'
              onClick={onShowRegister}
              className='text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-semibold transition-colors underline'
            >
              Register here
            </button>
          </div>
          <div className='mt-2 text-center text-xs text-gray-500 dark:text-gray-400'>
            Need help? Contact your administrator.
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
